import { isMockMode } from "@/lib/runtimeMode";

import type { SeededSession } from "./localDevSession";

/**
 * Mock mode's client-side session, deliberately parallel to
 * ./localDevSession.ts rather than shared with it.
 *
 * This is the one part of mock mode that has no admin-app equivalent to copy.
 * admin-app aliases the Clerk client to a stub in `next.config.js`, so a session
 * exists because the SDK says it does. web-app's session is a JWT that the
 * browser holds in local storage under `jasper-app-session`, established by the
 * `createSessionWithMFA` mutation — so with no backend there is nothing to
 * establish it, and every authenticated route bounces to /login.
 *
 * Seeding through the provider, the way local-dev already does, is what makes
 * that work without touching storage. The alternatives were worse:
 *
 * - Writing `jasper-app-session` from a boot component races
 *   `useLocalStorageWithListener`, which reads on mount, and it leaves a fake
 *   session behind in the developer's browser profile after mock mode is off.
 *   The Playwright suite does exactly this, but only because an init script
 *   runs before the app — the app has no equivalent.
 * - Auto-completing the login mutation inverts the dependency: it needs the mock
 *   GraphQL route serving before a session exists, and it cannot run on the
 *   routes wrapped in WithNoSessionOnRoute.
 *
 * Nothing verifies the token client-side — `hasAuthenticatedSession` is
 * `Boolean(session.token)`, with no decode and no server check — so a synthetic
 * value is enough. The token is still sent as `X-Authorization: Bearer: <token>`
 * by the real auth link; the mock GraphQL route ignores it.
 */

/**
 * Far future on purpose. `useSessionExpirationCheck` polls every 30s and
 * `ExpiredSession` redirects to /login the moment `Date.now()` passes this, so a
 * near date would silently log mock mode out mid-session. Matches the
 * `Session.expires` default the mock engine already uses.
 */
export const MOCK_SESSION_EXPIRES = "2100-01-01T01:00:50Z";

/**
 * Not a credential and not a JWT — nothing parses it. Spelled out so it is
 * obvious in devtools where the session came from.
 */
export const MOCK_SESSION_TOKEN = "mock-mode-session-token";

/**
 * Returns the session to run mock mode as, or null to start signed out.
 *
 * Signed in is the default because the authenticated shell is what frontend work
 * targets; `NEXT_PUBLIC_MOCK_SIGNED_OUT=1` opts out so the login and MFA flows
 * stay reachable against the mock GraphQL route. Inert outside mock mode.
 */
export function getMockSeededSession(): SeededSession | null {
	if (!isMockMode()) {
		return null;
	}

	if (process.env.NEXT_PUBLIC_MOCK_SIGNED_OUT === "1") {
		return null;
	}

	return {
		token: MOCK_SESSION_TOKEN,
		expires: MOCK_SESSION_EXPIRES,
		mfa: { required: false, challengeToken: "" },
	};
}
